import { AppNS, Dcra } from 'app';
// Implementation of this example ABAC (Attribute-Based Access Control) permission system:
// https://github.com/WebDevSimplified/permission-system/blob/main/auth-abac.ts
// see for more details and examples
import { merge } from 'lodash-es';

import { DOB_INSPECTIONS_ROLES } from './data/dobInspections';
import { DOB_INSPECTORS_ROLES } from './data/dobInspectors';
import { INSPECTIONS_ROLES } from './data/inspections';
import { MESSAGES_ROLES } from './data/messages';
import { SETTINGS_ROLES } from './data/settings';
import { TICKETS_ROLES } from './data/tickets';

type User = AppNS.User;
type Role = AppNS.Role;

type PermissionCheck<Key extends keyof Permissions> =
  | boolean
  | ((user: User, data: Permissions[Key]['dataType']) => boolean);

export type RolesWithPermissions = {
  [R in Role]: Partial<{
    [Key in keyof Permissions]: Partial<{
      [Action in Permissions[Key]['action']]: PermissionCheck<Key>;
    }>;
  }>;
};

type Permissions = {
  users: {
    dataType: AppNS.User;
    action: 'view' | 'create' | 'update' | 'delete' | 'loginAs';
  };
  inspections: {
    dataType: AppNS.Params;
    action: 'view' | 'create' /* => schedule */;
  };
  dobInspections: {
    dataType: Dcra.Inspection;
    action: 'view' | 'create' | 'update' | 'delete' | 'cancel' | 'editPermits';
  };
  dobInspectors: {
    dataType: AppNS.Inspector;
    action: 'view' | 'create' | 'update' | 'delete' | 'deactivate';
  };
  messages: {
    dataType: AppNS.Message;
    action: 'view' | 'create';
  };
  tickets: {
    dataType: AppNS.Ticket;
    action: 'view' | 'create' | 'update' | 'assign';
  };
  settings: {
    dataType: AppNS.Params;
    action: 'view' | 'updateHolidays';
  };
};

const ROLES: RolesWithPermissions = merge(
  DOB_INSPECTIONS_ROLES,
  INSPECTIONS_ROLES,
  DOB_INSPECTORS_ROLES,
  MESSAGES_ROLES,
  TICKETS_ROLES,
  SETTINGS_ROLES
);

export function hasPermission<Resource extends keyof Permissions>(
  user: AppNS.User | null | undefined,
  resource: Resource,
  action: Permissions[Resource]['action'],
  data?: Permissions[Resource]['dataType']
) {
  if (!user) return false;

  return user.roles.some((role) => {
    const permission = (ROLES as RolesWithPermissions)[role][resource]?.[action];
    if (permission == null) return false;

    if (typeof permission === 'boolean') return permission;
    return data != null && permission(user, data);
  });
}
